/**
 * Simple in-memory cache for expensive queries
 * Reduces database load for frequently accessed data
 */

class QueryCache {
  constructor() {
    this.cache = new Map();
    this.timestamps = new Map();
  }

  /**
   * Get cached value if valid
   * @param {string} key - Cache key
   * @param {number} maxAge - Max age in milliseconds
   * @returns {*} Cached value or null
   */
  get(key, maxAge = 60000) { // Default 1 minute
    const timestamp = this.timestamps.get(key);
    if (!timestamp) return null;

    const age = Date.now() - timestamp;
    if (age > maxAge) {
      this.delete(key);
      return null;
    }

    return this.cache.get(key);
  }

  /**
   * Set cached value
   * @param {string} key - Cache key
   * @param {*} value - Value to cache
   */
  set(key, value) {
    this.cache.set(key, value);
    this.timestamps.set(key, Date.now());
  }

  /**
   * Delete cached value
   * @param {string} key - Cache key
   */
  delete(key) {
    this.cache.delete(key);
    this.timestamps.delete(key);
  }

  /**
   * Clear all cache
   */
  clear() {
    this.cache.clear();
    this.timestamps.clear();
  }

  /**
   * Clear expired entries
   * @param {number} maxAge - Max age in milliseconds
   */
  clearExpired(maxAge = 60000) {
    const now = Date.now();
    for (const [key, timestamp] of this.timestamps.entries()) {
      if (now - timestamp > maxAge) {
        this.delete(key);
      }
    }
  }

  /**
   * Get or set cached value
   * @param {string} key - Cache key
   * @param {Function} fn - Function to call if cache miss
   * @param {number} maxAge - Max age in milliseconds
   * @returns {Promise<*>} Cached or fresh value
   */
  async getOrSet(key, fn, maxAge = 60000) {
    const cached = this.get(key, maxAge);
    if (cached !== null) {
      console.log(`✅ Cache HIT: ${key}`);
      return cached;
    }

    console.log(`❌ Cache MISS: ${key}`);
    const value = await fn();
    this.set(key, value);
    return value;
  }
}

// Create singleton instance
const cache = new QueryCache();

// Clear expired entries every 5 minutes
setInterval(() => {
  cache.clearExpired(300000); // 5 minutes
}, 300000);

export default cache;

// Helper function to create cache keys
export function createCacheKey(prefix, ...args) {
  return `${prefix}:${args.map(a => String(a)).join(':')}`;
}

// Specialized cache instances with different TTLs
export const caches = {
  // Short cache for frequently changing data (1 minute)
  short: {
    get: (key) => cache.get(key, 60000),
    set: (key, value) => cache.set(key, value),
    getOrSet: (key, fn) => cache.getOrSet(key, fn, 60000)
  },
  
  // Medium cache for moderately stable data (5 minutes)
  medium: {
    get: (key) => cache.get(key, 300000),
    set: (key, value) => cache.set(key, value),
    getOrSet: (key, fn) => cache.getOrSet(key, fn, 300000)
  },
  
  // Long cache for stable data (15 minutes)
  long: {
    get: (key) => cache.get(key, 900000),
    set: (key, value) => cache.set(key, value),
    getOrSet: (key, fn) => cache.getOrSet(key, fn, 900000)
  }
};

